"use client";

import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { BookmarkCategory, BOOKMARK_CATEGORIES } from "@/lib/api/public-client";
import { usePublicAuthSafe } from "@/lib/auth/PublicAuthContext";
import { useBookmarksSafe } from "@/lib/bookmarks/BookmarkContext";
import { useReactionsSafe } from "@/lib/reactions/ReactionsContext";
import LoginRegister from "@/components/component_form/LoginRegisterForm";
import OTPForm from "@/components/component_form/OTPForm";
type MessageIconLogoVariant = 'message-icon-3' | 'message-icon-4';
interface MessageIconLogoProps {
  id: string;
  variant?: MessageIconLogoVariant;
  // Bookmark & Reaction props
  slug?: string;
  category?: BookmarkCategory;
  title?: string;
  thumbnailUrl?: string;
  description?: string;
  articleUrl?: string;
}
export const MessageIconLogo = ({
  id,
  variant = 'message-icon-3',
  slug,
  category = BOOKMARK_CATEGORIES.NEWS,
  title,
  thumbnailUrl,
  description,
  articleUrl
}: MessageIconLogoProps) => {
  const authContext = usePublicAuthSafe();
  const isAuthenticated = authContext?.isAuthenticated ?? false;
  const bookmarkContext = useBookmarksSafe();
  const reactionsContext = useReactionsSafe();
  const [isBookmarkLoading, setIsBookmarkLoading] = useState(false);
  const [isLikeLoading, setIsLikeLoading] = useState(false);
  const [isDislikeLoading, setIsDislikeLoading] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [loginStep, setLoginStep] = useState<'email' | 'otp'>('email');
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  const iconColor = variant === 'message-icon-3' ? 'var(--color-020202)' : 'var(--color-FFFFFF)';

  // Bookmark status
  const isBookmarked = slug && bookmarkContext ? bookmarkContext.isBookmarked(slug) : false;
  const bookmarkFillColor = isBookmarked ? iconColor : 'none';

  // Reaction status
  const isLiked = slug && reactionsContext ? reactionsContext.isLiked(slug) : false;
  const isDisliked = slug && reactionsContext ? reactionsContext.isDisliked(slug) : false;
  const likeFillColor = isLiked ? '#22C55E' : 'none'; // Green when liked
  const dislikeFillColor = isDisliked ? '#EF4444' : 'none'; // Red when disliked
  const likeStrokeColor = isLiked ? '#22C55E' : iconColor;
  const dislikeStrokeColor = isDisliked ? '#EF4444' : iconColor;
  const handleBookmarkToggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!slug || !bookmarkContext) return;
    if (!isAuthenticated) {
      setLoginStep('email');
      setShowLoginPopup(true);
      return;
    }
    if (isBookmarkLoading) return;
    setIsBookmarkLoading(true);
    try {
      await bookmarkContext.toggleBookmark({
        slug,
        category,
        title,
        thumbnail_url: thumbnailUrl,
        description,
        article_url: articleUrl
      });
    } catch (error) {
      console.error("Failed to toggle bookmark:", error);
    } finally {
      setIsBookmarkLoading(false);
    }
  };
  const handleLikeToggle = async (e_0: React.MouseEvent) => {
    e_0.preventDefault();
    e_0.stopPropagation();
    if (!slug || !reactionsContext) return;
    if (!isAuthenticated) {
      setLoginStep('email');
      setShowLoginPopup(true);
      return;
    }
    if (isLikeLoading) return;
    setIsLikeLoading(true);
    try {
      await reactionsContext.toggleLike({
        slug,
        category,
        title,
        thumbnail_url: thumbnailUrl,
        description,
        article_url: articleUrl
      });
    } catch (error_0) {
      console.error("Failed to toggle like:", error_0);
    } finally {
      setIsLikeLoading(false);
    }
  };
  const handleDislikeToggle = async (e_1: React.MouseEvent) => {
    e_1.preventDefault();
    e_1.stopPropagation();
    if (!slug || !reactionsContext) return;
    if (!isAuthenticated) {
      setLoginStep('email');
      setShowLoginPopup(true);
      return;
    }
    if (isDislikeLoading) return;
    setIsDislikeLoading(true);
    try {
      await reactionsContext.toggleDislike({
        slug,
        category,
        title,
        thumbnail_url: thumbnailUrl,
        description,
        article_url: articleUrl
      });
    } catch (error_1) {
      console.error("Failed to toggle dislike:", error_1);
    } finally {
      setIsDislikeLoading(false);
    }
  };
  return <>
      {/* Login Popup Modal - Using Portal to render at document body */}
      {mounted && showLoginPopup && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: '100vw',
      height: '100vh'
    }} onClick={e_2 => {
      e_2.preventDefault();
      e_2.stopPropagation();
      setShowLoginPopup(false);
      setLoginStep('email');
    }}>
          <div className="relative w-full max-w-md lg:max-w-[537px] max-h-[90vh] overflow-y-auto bg-white shadow-lg mx-4 rounded-lg" onClick={e_3 => e_3.stopPropagation()}>
            {loginStep === 'email' ? <LoginRegister onClose={() => {
          setShowLoginPopup(false);
          setLoginStep('email');
        }} onContinue={() => setLoginStep('otp')} /> : <OTPForm onClose={() => {
          setShowLoginPopup(false);
          setLoginStep('email');
        }} onSuccess={() => {
          setShowLoginPopup(false);
          setLoginStep('email');
        }} />}
          </div>
        </div>, document.body)}
      <div className="flex gap-2 w-fit">
        {/* Like Icon - HIDDEN */}
        {/* <button
          onClick={handleLikeToggle}
          disabled={isLikeLoading || !slug}
          className={`p-0 border-0 bg-transparent transition-transform duration-200 ${
            isLikeLoading ? "opacity-50 cursor-wait" : slug ? "cursor-pointer hover:scale-110" : "cursor-default"
          }`}
          aria-label={isLiked ? "Remove like" : "Like"}
         >
          <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill={likeFillColor}
            stroke={likeStrokeColor}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M7 10v12" />
            <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z" />
          </svg>
         </button> */}

        {/* Dislike Icon - HIDDEN */}
        {/* <button
          onClick={handleDislikeToggle}
          disabled={isDislikeLoading || !slug}
          className={`p-0 border-0 bg-transparent transition-transform duration-200 ${
            isDislikeLoading ? "opacity-50 cursor-wait" : slug ? "cursor-pointer hover:scale-110" : "cursor-default"
          }`}
          aria-label={isDisliked ? "Remove dislike" : "Dislike"}
         >
          <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill={dislikeFillColor}
            stroke={dislikeStrokeColor}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M17 14V2" />
            <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22h0a3.13 3.13 0 0 1-3-3.88Z" />
          </svg>
         </button> */}

        {/* Bookmark Icon */}
        <button onClick={handleBookmarkToggle} disabled={isBookmarkLoading || !slug} className={`p-0 border-0 bg-transparent transition-transform duration-200 ${isBookmarkLoading ? "opacity-50 cursor-wait" : slug ? "cursor-pointer hover:scale-110" : "cursor-default"}`} aria-label={isBookmarked ? "Remove bookmark" : "Add bookmark"}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill={bookmarkFillColor} stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m19 21-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z" />
          </svg>
        </button>
      </div>
    </>;
};